import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { USER_DETAIL } from "@/constants/testIds";
import { toast } from "sonner";
import {
  BrainCircuit, Info, RefreshCw, Sparkles, AlertTriangle,
  ChevronDown, ChevronUp, History, X, Play,
} from "lucide-react";

/** Show a numeric metric with clear "unavailable" state — never a fake 0. */
function Metric({ label, value, available = true, help, testId }) {
  if (!available || value === null || value === undefined) {
    return (
      <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
        <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-sm text-slate-600 font-semibold mt-1" data-testid={testId}>
          Not enough evidence
        </div>
        {help && <div className="text-[10px] text-slate-500 mt-1">{help}</div>}
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-heading font-extrabold text-2xl tabular text-slate-800 mt-0.5" data-testid={testId}>
        {value}
      </div>
      {help && <div className="text-[10px] text-slate-500 mt-1">{help}</div>}
    </div>
  );
}

/** Formats a component row inside the breakdown drawer. */
function ComponentRow({ name, comp, configuredWeight, appliedWeight, unavailableReason }) {
  const isAvailable =
    comp && (comp.available !== false) && comp.value !== null && comp.value !== undefined;
  const contribution = isAvailable && appliedWeight != null
    ? Math.round(comp.value * appliedWeight * 100) / 100
    : null;
  const weightChanged =
    configuredWeight != null && appliedWeight != null &&
    Math.abs(configuredWeight - appliedWeight) > 1e-4;

  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-start justify-between">
        <div className="font-semibold text-sm text-slate-800">{name}</div>
        {!isAvailable && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
            Unavailable
          </span>
        )}
      </div>
      {isAvailable ? (
        <>
          <div className="tabular text-slate-700 mt-1 text-sm">
            Value: <b>{Math.round(comp.value * 10) / 10}</b>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Configured weight: {Math.round((configuredWeight ?? 0) * 100)}%
            {weightChanged && (
              <>
                {" "}· Applied: <b className="text-orange-700">{Math.round(appliedWeight * 100)}%</b>
              </>
            )}
          </div>
          {contribution !== null && (
            <div className="text-orange-700 font-semibold tabular text-sm mt-1">
              Weighted contribution: {contribution}
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-slate-500 mt-1">
          {unavailableReason || comp?.reason || "Signal not observable yet."}
          {weightChanged && (
            <div className="text-orange-700 mt-1">
              Weights adjusted because {name} was unavailable.
            </div>
          )}
        </div>
      )}
      {comp?.fallback && comp?.reason && isAvailable && (
        <div className="text-[10px] text-slate-500 mt-1">Fallback: {comp.reason}</div>
      )}
    </div>
  );
}

/** Compose a concise human-readable "Why this score?" summary. */
function whyThisScore(preview) {
  if (!preview) return null;
  const c = preview.components || {};
  const bits = [];

  const aiSignals = preview.ai_signals_ref || null;
  if (preview.insufficient_ai_evidence) {
    bits.push("No valid AI structured analysis yet — score is provisional and excludes AI evidence.");
  } else if (aiSignals?.reasoning_summary) {
    bits.push(`AI read: ${aiSignals.reasoning_summary}`);
  }

  const life = c.lifecycle_opportunity;
  if (life?.day) bits.push(`Trial day ${life.day} (lifecycle score ${Math.round(life.value)}).`);

  const mom = c.momentum;
  if (mom?.fallback) {
    bits.push("Momentum: no user activity signals available.");
  } else if (mom?.value !== undefined) {
    const parts = [];
    if (mom.conversation_recency !== null && mom.conversation_recency !== undefined) {
      parts.push(`recency ${Math.round(mom.conversation_recency)}`);
    } else if (mom.conversation_recency_source === "unavailable_no_user_inbound") {
      parts.push("no user inbound yet");
    }
    if (mom.app_recency !== null && mom.app_recency !== undefined) parts.push(`app ${Math.round(mom.app_recency)}`);
    if (parts.length) bits.push(`Momentum (${parts.join(", ")}): ${Math.round(mom.value)}.`);
  }

  const eng = c.engagement;
  if (eng?.value !== undefined) {
    bits.push(`Engagement ${Math.round(eng.value)} · ${eng.meaningful_reply_count || 0} meaningful reply(ies) across ${eng.distinct_active_days || 0} active day(s).`);
  }

  const ss = c.strong_signal_adjustment;
  if (ss?.applied?.length) {
    bits.push(`Strong signal ${ss.value > 0 ? "+" : ""}${ss.value}: ${ss.applied.map(a => a.key).join(", ")}.`);
  }
  if (aiSignals?.active_objection) {
    bits.push(`Active objection${aiSignals.objection_type ? " (" + aiSignals.objection_type + ")" : ""}${aiSignals.objection_resolved ? " — appears resolved" : ""}.`);
  }
  return bits.join(" ");
}


export default function CpsV2Panel({ userId, legacyScore }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "super_admin";

  const [preview, setPreview] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showLegacyCompare, setShowLegacyCompare] = useState(false);
  const [history, setHistory] = useState({ items: [], has_more: false, next_cursor: null });
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch the current preview (deterministic; no AI call). If dedup skips
      // a snapshot the response still carries the current computed values.
      const [{ data: prev }, { data: cfg }] = await Promise.all([
        api.post(`/trial-users/${userId}/cps-v2-preview`),
        api.get(`/scoring-config`),
      ]);
      setPreview(prev);
      setConfig(cfg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load CPS V2");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const loadHistory = async (cursor = null) => {
    setHistoryLoading(true);
    try {
      const params = { limit: 20 };
      if (cursor) params.before = cursor;
      const { data } = await api.get(`/trial-users/${userId}/cps-v2-history`, { params });
      setHistory((prev) => cursor
        ? { ...data, items: [...prev.items, ...data.items] }
        : data);
    } catch (e) {
      toast.error("History load failed");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (showHistory && history.items.length === 0) loadHistory();
    // eslint-disable-next-line
  }, [showHistory]);

  const mode = config?.mode || "legacy";
  const provisional = !!preview?.cps_v2_provisional;
  const insufficient = !!preview?.insufficient_ai_evidence;
  const readinessAvailable = preview?.readiness_available !== false;
  const cps = preview?.cps_v2;
  const readiness = preview?.readiness;
  const confidence = preview?.confidence;
  const configV = preview?.config_version;

  const whyText = useMemo(() => whyThisScore(preview), [preview]);

  const modeLabel = mode === "shadow"
    ? "CPS V2 — Shadow"
    : mode === "v2" ? "CPS V2 — Production" : "CPS V2 — Dormant (Legacy mode)";

  return (
    <div
      data-testid={USER_DETAIL.cpsV2Panel}
      className="mt-4 border-2 border-dashed border-indigo-200 bg-indigo-50/40 rounded-lg p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-4 w-4 text-indigo-600" />
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-700"
               data-testid={USER_DETAIL.cpsV2ShadowLabel}>
            {modeLabel}
          </div>
          {mode !== "legacy" && (
            <span className="text-[9px] font-mono text-slate-500">v{configV || "?"}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-indigo-700 disabled:opacity-60"
            title="Recompute deterministic CPS V2 (no AI call)"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Recompute
          </button>
          {isAdmin && (
            <button
              data-testid={USER_DETAIL.cpsV2ComparisonToggle}
              onClick={() => setShowLegacyCompare((s) => !s)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-indigo-700"
              title="Show legacy vs V2 side-by-side"
            >
              <Sparkles className="h-3 w-3" /> {showLegacyCompare ? "Hide" : "Show"} Legacy vs V2
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-indigo-700"
              title="Snapshot history"
            >
              <History className="h-3 w-3" /> History
            </button>
          )}
        </div>
      </div>

      {mode === "legacy" && (
        <div className="mt-2 text-[11px] text-slate-600">
          V2 engine is dormant. Preview endpoint still runs deterministically for diagnostic use.
        </div>
      )}

      {/* Provisional banner */}
      {(provisional || insufficient) && (
        <div className="mt-3 flex items-start gap-2 border border-amber-300 bg-amber-50 rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-[11px] text-amber-800">
            <div className="font-semibold" data-testid={USER_DETAIL.cpsV2ProvisionalBadge}>
              Provisional — Insufficient AI Evidence
            </div>
            <div>
              No validated AI structured analysis yet. AI-Intent excluded and the remaining component weights renormalized. Treat the numeric score as diagnostic only.
            </div>
          </div>
        </div>
      )}

      {/* Score row */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className={`border rounded-md p-3 ${(provisional || insufficient) ? "border-amber-300 bg-white" : "border-indigo-200 bg-white"}`}>
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">CPS V2</div>
          <div className={`font-heading font-extrabold text-3xl tabular ${provisional || insufficient ? "text-amber-600 opacity-80" : "text-indigo-700"}`}
               data-testid={USER_DETAIL.cpsV2Score}>
            {cps ?? "—"}
          </div>
          {preview?.cps_v2_precise !== undefined && (
            <div className="text-[10px] text-slate-500 mt-0.5 tabular">precise: {preview.cps_v2_precise}</div>
          )}
        </div>
        <Metric
          label="Readiness"
          value={readiness}
          available={readinessAvailable && readiness !== null}
          testId={USER_DETAIL.cpsV2Readiness}
          help={!readinessAvailable ? "No valid AI signals" : undefined}
        />
        <Metric
          label="Confidence"
          value={confidence}
          available={confidence !== null && confidence !== undefined}
          testId={USER_DETAIL.cpsV2Confidence}
        />
      </div>

      {/* Side-by-side legacy comparison for admins */}
      {isAdmin && showLegacyCompare && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid={USER_DETAIL.cpsV2LegacyBadge}>
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Legacy Score (PRODUCTION)</div>
            <div className="font-heading font-extrabold text-3xl tabular text-orange-600">{legacyScore ?? "—"}</div>
            <div className="text-[10px] text-slate-500 mt-1">This is what conversation flows still use.</div>
          </div>
          <div className="border border-slate-200 rounded-md p-3 bg-white">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">CPS V2 (Shadow)</div>
            <div className={`font-heading font-extrabold text-3xl tabular ${provisional || insufficient ? "text-amber-600 opacity-80" : "text-indigo-700"}`}>
              {cps ?? "—"}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              Delta: <b className={cps != null && legacyScore != null ? (cps - legacyScore > 0 ? "text-emerald-600" : cps - legacyScore < 0 ? "text-red-600" : "text-slate-500") : ""}>
                {cps != null && legacyScore != null ? (cps - legacyScore > 0 ? `+${cps - legacyScore}` : cps - legacyScore) : "—"}
              </b>
            </div>
          </div>
        </div>
      )}

      {/* Why this score? + Breakdown toggle */}
      {whyText && (
        <div className="mt-3 text-xs text-slate-700 border-l-2 border-indigo-300 pl-3 py-1 bg-white/60 rounded-r">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Why this score?</div>
          <div>{whyText}</div>
        </div>
      )}

      <button
        data-testid={USER_DETAIL.cpsV2WhyBtn}
        onClick={() => setShowBreakdown((s) => !s)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
      >
        {showBreakdown ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {showBreakdown ? "Hide" : "Show"} full breakdown
      </button>

      {/* Full breakdown drawer */}
      {showBreakdown && preview && (
        <div
          data-testid={USER_DETAIL.cpsV2BreakdownDrawer}
          className="mt-3 border-t border-indigo-200 pt-3"
        >
          {preview.weight_normalization === "ai_intent_excluded_and_renormalized" && (
            <div className="mb-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Weights renormalized because AI evidence was unavailable. Applied weights below reflect the adjustment.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ComponentRow
              name="AI Conversion Intent"
              comp={preview.components?.ai_intent}
              configuredWeight={config?.weights?.ai_intent}
              appliedWeight={preview.applied_weights?.ai_intent}
              unavailableReason={preview.components?.ai_intent?.available === false ? "No valid AI structured analysis yet." : undefined}
            />
            <ComponentRow
              name="Engagement"
              comp={preview.components?.engagement}
              configuredWeight={config?.weights?.engagement}
              appliedWeight={preview.applied_weights?.engagement}
            />
            <ComponentRow
              name="Momentum"
              comp={preview.components?.momentum}
              configuredWeight={config?.weights?.momentum}
              appliedWeight={preview.applied_weights?.momentum}
            />
            <ComponentRow
              name="Lifecycle Opportunity"
              comp={preview.components?.lifecycle_opportunity}
              configuredWeight={config?.weights?.lifecycle_opportunity}
              appliedWeight={preview.applied_weights?.lifecycle_opportunity}
            />
          </div>

          {/* Strong signal — always shown, its adjustment sits OUTSIDE weights */}
          <div className="mt-3 border border-slate-200 rounded-md p-3">
            <div className="font-semibold text-sm text-slate-800">Strong Signal Adjustment</div>
            <div className="tabular text-slate-700 mt-1 text-sm">
              Applied: <b className={preview.components?.strong_signal_adjustment?.value > 0 ? "text-emerald-700" : preview.components?.strong_signal_adjustment?.value < 0 ? "text-red-700" : "text-slate-500"}>
                {preview.components?.strong_signal_adjustment?.value ?? 0}
              </b> <span className="text-slate-500 text-[11px]">(bounded to ±10)</span>
            </div>
            {preview.components?.strong_signal_adjustment?.applied?.length > 0 && (
              <div className="text-[11px] text-slate-600 mt-1">
                Reasons: {preview.components.strong_signal_adjustment.applied.map(a => `${a.key} (${a.value > 0 ? "+" : ""}${a.value})`).join(", ")}
              </div>
            )}
            {preview.components?.strong_signal_adjustment?.suppressed?.length > 0 && (
              <div className="text-[10px] text-slate-500 mt-1">
                Deduplicated: {preview.components.strong_signal_adjustment.suppressed.map(s => s.key).join(", ")}
              </div>
            )}
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            <div className="border border-slate-200 rounded-md p-3">
              <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Base CPS</div>
              <div className="tabular font-semibold text-slate-800">{preview.base_cps ?? "—"}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Final CPS V2</div>
              <div className="tabular font-semibold text-indigo-700">{preview.cps_v2 ?? "—"}</div>
              <div className="text-[10px] text-slate-500">precise: {preview.cps_v2_precise ?? "—"}</div>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500">Mode / Config v</div>
              <div className="font-mono text-slate-700 text-sm">{preview.mode} · v{preview.config_version}</div>
            </div>
          </div>
        </div>
      )}

      {/* Admin: snapshot history table */}
      {isAdmin && showHistory && (
        <div
          data-testid={USER_DETAIL.cpsV2HistoryPanel}
          className="mt-4 border-t border-indigo-200 pt-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Snapshot history — legacy vs V2
            </div>
            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-700">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {history.items.length === 0 && !historyLoading && (
            <div className="text-xs text-slate-500 italic py-4 text-center">No snapshots yet.</div>
          )}
          {history.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5 pr-2">When</th>
                    <th className="pr-2">Trigger</th>
                    <th className="pr-2">Mode</th>
                    <th className="pr-2">v</th>
                    <th className="pr-2 text-right">Legacy</th>
                    <th className="pr-2 text-right">CPS V2</th>
                    <th className="pr-2 text-right">Δ</th>
                    <th className="pr-2 text-right">Read.</th>
                    <th className="pr-2 text-right">Conf.</th>
                    <th className="pr-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {history.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-white">
                      <td className="py-1.5 pr-2 whitespace-nowrap font-mono text-slate-600">
                        {new Date(it.created_at).toLocaleString()}
                      </td>
                      <td className="pr-2 text-slate-700">{it.trigger}</td>
                      <td className="pr-2 font-mono text-slate-600">{it.mode}</td>
                      <td className="pr-2 font-mono text-slate-500">{it.config_version}</td>
                      <td className="pr-2 text-right tabular text-orange-600 font-semibold">{it.legacy_score ?? "—"}</td>
                      <td className={`pr-2 text-right tabular font-semibold ${it.provisional ? "text-amber-600" : "text-indigo-700"}`}>
                        {it.cps_v2 ?? "—"}
                      </td>
                      <td className={`pr-2 text-right tabular ${it.score_delta > 0 ? "text-emerald-600" : it.score_delta < 0 ? "text-red-600" : "text-slate-500"}`}>
                        {it.score_delta > 0 ? `+${it.score_delta}` : it.score_delta ?? "—"}
                      </td>
                      <td className="pr-2 text-right tabular text-slate-700">{it.readiness ?? "—"}</td>
                      <td className="pr-2 text-right tabular text-slate-700">{it.confidence ?? "—"}</td>
                      <td className="pr-2">
                        {it.provisional && (
                          <span className="text-[9px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">provisional</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {history.has_more && (
            <button
              data-testid={USER_DETAIL.cpsV2HistoryLoadMore}
              disabled={historyLoading}
              onClick={() => loadHistory(history.next_cursor)}
              className="mt-2 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-60"
            >
              {historyLoading ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
